import type {
  EditReclamationStep,
  ReclamationStep,
  TrackingEventType,
} from 'common/enums/Reclamation';
import { TrackingEvent, TrackingEventElement } from 'common/enums/Reclamation';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import type { Element } from 'common/types/tracking';
import type { UseFormSubmitHandlerParams } from '../hooks/useFormSubmitHandler';

export interface CommonTrackingEventData {
  type?: TrackingEventType;
  title?: ReclamationStep | EditReclamationStep;
}

interface DispatchCloseDrawerEventParams extends CommonTrackingEventData {
  options: CloseEventOptions;
}

interface DispatchSubmitStepParams extends CommonTrackingEventData {
  label?: string;
  button_text?: string;
}

interface DispatchAdditionalInfoClickEventParams extends CommonTrackingEventData {
  link_text: string;
}

type GetCommonTrackingEventDataParams = Pick<UseFormSubmitHandlerParams, 'state' | 'activeStep'>;

export interface ReasonsTrackingEventData extends CommonTrackingEventData {
  button_text?: string;
}

export interface SolutionsTrackingEventData extends CommonTrackingEventData {
  button_text?: string;
}

export type AdditionalInfoTrackingEventData = CommonTrackingEventData;

export interface CloseEventOptions {
  element: TrackingEventElement | Element;
}

export const dispatchCloseDrawerEvent = ({
  type,
  title,
  options: { element },
}: DispatchCloseDrawerEventParams) =>
  dispatchTrackingEventGA4({
    event: TrackingEvent.ReclamationDrawerClick,
    interaction: 'close',
    element,
    ...(type && { type }),
    ...(title && { title }),
  });

export const dispatchGoBackButtonEvent = (type: TrackingEventType, title: ReclamationStep) =>
  dispatchTrackingEventGA4({
    event: TrackingEvent.ReclamationDrawerClick,
    element: 'link',
    interaction: 'back',
    ...(type && { type }),
    ...(title && { title }),
  });

export const dispatchSubmitStepEvent = ({
  type,
  title,
  label,
  button_text,
}: DispatchSubmitStepParams) =>
  dispatchTrackingEventGA4({
    event: TrackingEvent.ReclamationDrawerClick,
    element: TrackingEventElement.Button,
    ...(type && { type }),
    ...(title && { title }),
    ...(label && { label }),
    ...(button_text && { button_text }),
  });

export const dispatchAdditionalInfoClickEvent = ({
  type,
  title,
  link_text,
}: DispatchAdditionalInfoClickEventParams) =>
  dispatchTrackingEventGA4({
    event: TrackingEvent.ReclamationDrawerClick,
    element: TrackingEventElement.Link,
    link_text,
    ...(type && { type }),
    ...(title && { title }),
  });

export const getCommonTrackingEventData = ({
  state,
  activeStep,
}: GetCommonTrackingEventDataParams): CommonTrackingEventData => ({
  type: state?.trackingEventType,
  title: activeStep?.type,
});
